import { useLocale, useT } from '../../i18n'
import type { StockCountDifference } from '../../utils/reportDatasets'

// Findings #222: "Stock reconciliado" explained on the screen, for whoever
// uses it rarely, and honest when there is nothing to reconcile.

export type StockDifferenceSummary = { counts: number; fish: number }

export const summarizeStockDifferences = (differences: readonly StockCountDifference[]): StockDifferenceSummary => ({
  counts: differences.length,
  fish: differences.reduce((sum, entry) => sum + entry.difference, 0)
})

export const formatFishDifference = (fish: number, locale: string): string => (
  `${fish > 0 ? '+' : fish < 0 ? '−' : ''}${Math.abs(fish).toLocaleString(locale)}`
)

const HowItWorks = () => {
  const t = useT()
  return (
    <details style={{ marginTop: 6, fontSize: 13 }}>
      <summary style={{ cursor: 'pointer', color: '#045F6D', fontWeight: 600 }}>{t('reports.stockView.howTo')}</summary>
      <div style={{ display: 'grid', gap: 6, marginTop: 6, padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: '#f8fafc' }}>
        <div>{t('reports.stockView.example')}</div>
        <div>{t('reports.stockView.useFor')}</div>
        <div>{t('reports.stockView.noChange')}</div>
        <div>{t('reports.stockView.needs')}</div>
      </div>
    </details>
  )
}

const NoDifferences = () => {
  const t = useT()
  return (
    <div data-testid="stock-view-none" style={{ marginTop: 6, padding: 8, borderRadius: 8, fontSize: 13, backgroundColor: '#fffbeb', color: '#92400e' }}>
      ⚠ {t('reports.stockView.none')}
    </div>
  )
}

export default function StockViewExplainer({ reconciled, differences }: { reconciled: boolean; differences: StockDifferenceSummary | null }) {
  const t = useT()
  return (
    <div style={{ marginTop: 6 }}>
      <div className="lead" style={{ fontSize: 13 }}>{t('reports.stockView.intro')}</div>
      <HowItWorks />
      {reconciled && differences?.counts === 0 && <NoDifferences />}
    </div>
  )
}

/** One line above a reconciled report's results. */
export function StockViewSummary({ differences }: { differences: StockDifferenceSummary | null }) {
  const t = useT()
  const locale = useLocale()
  if (!differences) return null
  return (
    <div style={{ marginTop: 10 }}>
      {differences.counts === 0 ? <NoDifferences /> : (
        <div className="lead" data-testid="stock-view-summary" style={{ fontSize: 13 }}>
          {t('reports.stockView.summary', { counts: differences.counts, fish: formatFishDifference(differences.fish, locale) })}
        </div>
      )}
      <HowItWorks />
    </div>
  )
}
